import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";

/**
 * Cloudflare Turnstile widget — a controlled hook + component pair.
 *
 * Fetches the sitekey once at runtime from `GET /api/config/turnstile`. If
 * `sitekey` is null (env not configured), the hook returns null and callers
 * silently submit their forms without a `turnstile_token`. The backend already
 * skips verification when `TURNSTILE_SECRET` is unset.
 *
 * Usage:
 *   const { widgetRef, token, reset } = useTurnstile();
 *   ...
 *   <TurnstileMount widgetRef={widgetRef} />
 *   ...
 *   await api.post(..., { ..., turnstile_token: token });
 *   reset(); // after an error, so the user gets a fresh challenge
 */
let _cachedSitekey; // memoize across component mounts

async function fetchSitekey() {
  if (_cachedSitekey !== undefined) return _cachedSitekey;
  try {
    const r = await api.get("/config/turnstile");
    _cachedSitekey = r.data?.sitekey || null;
  } catch {
    _cachedSitekey = null;
  }
  return _cachedSitekey;
}

export function useTurnstile() {
  const widgetRef = useRef(null);
  const widgetIdRef = useRef(null);
  const [sitekey, setSitekey] = useState(_cachedSitekey);
  const [token, setToken] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      const k = await fetchSitekey();
      if (alive) setSitekey(k);
    })();
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (!sitekey || !widgetRef.current || widgetIdRef.current) return;
    // Turnstile script loads via <script async> — poll briefly if not ready yet.
    const tryRender = () => {
      if (!window.turnstile) return false;
      widgetIdRef.current = window.turnstile.render(widgetRef.current, {
        sitekey,
        theme: "auto",
        callback: (t) => setToken(t),
        "expired-callback": () => setToken(null),
        "error-callback": () => setToken(null),
      });
      return true;
    };
    if (tryRender()) return;
    const iv = setInterval(() => {
      if (tryRender()) clearInterval(iv);
    }, 200);
    return () => clearInterval(iv);
  }, [sitekey]);

  const reset = () => {
    setToken(null);
    if (window.turnstile && widgetIdRef.current) {
      try { window.turnstile.reset(widgetIdRef.current); } catch { /* noop */ }
    }
  };

  return { widgetRef, sitekey, token, reset };
}

export function TurnstileMount({ widgetRef, sitekey }) {
  // Nothing renders when Turnstile isn't configured — the parent form just
  // POSTs without a token and the backend no-ops the verify.
  if (!sitekey) return null;
  return (
    <div
      ref={widgetRef}
      className="cf-turnstile my-3"
      data-sitekey={sitekey}
      data-theme="auto"
      data-testid="turnstile-widget"
    />
  );
}
